/**
 * V2 header bell — unread yoinks from explorers you follow.
 *
 * The button IS the indicator: it renders only while something is unread
 * (like a message badge). Opening it shows the new yoinks and KEEPS the
 * count — each row you open counts the badge down by one, and reopening
 * the bell lists only what's still unread. "Clear all" resets the lot.
 *
 * The shell is built once and updated in place (no innerHTML wipe on
 * store changes) so the open dialog can never be yanked out from under
 * the reader by an unrelated store write.
 */
import { escapeHtml } from '../../assets/js/utils.js';
import * as gateway from '../core/gateway.js';
import { icon, categoryIcon } from './icons.js';
import { thumbUrl } from './card.js';
import { markSeen, markSeenOne, UNREAD_CAP } from '../features/follows/unread.js';

function rowHtml(yoink) {
  const who = yoink.public_profiles?.full_name || yoink.public_profiles?.username || 'Anonymous';
  const date = new Date(yoink.created_at);
  const when = Number.isNaN(date.getTime())
    ? ''
    : date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  const img = Array.isArray(yoink.images) ? yoink.images[0] : null;
  const url = img ? (img.startsWith('http') ? img : gateway.getPublicUrl('yoinks', img)) : null;
  const thumb = url
    ? `<img class="v2-alert-thumb" src="${thumbUrl(url, 160)}" alt="" loading="lazy"
        data-fallback-src="${url}" data-fallback-cat="${escapeHtml(yoink.categories?.name || '')}">`
    : `<span class="v2-alert-thumb log-thumb-blank">${categoryIcon(yoink.categories, 22)}</span>`;
  return `
    <button type="button" class="v2-alert-row" data-alert-yoink="${yoink.id}">
      ${thumb}
      <span class="v2-alert-text">
        <span class="v2-alert-title">${escapeHtml(yoink.title || 'Untitled')}</span>
        <span class="v2-alert-byline">${escapeHtml(who)}${when ? ` · ${when}` : ''}</span>
      </span>
    </button>`;
}

export function mount(el, ctx) {
  const { store, router } = ctx;

  el.innerHTML = `
    <button type="button" class="v2-alerts-btn" data-alerts-open hidden>
      ${icon('bell', 22)}
      <span class="v2-alerts-count" aria-hidden="true"></span>
    </button>
    <dialog class="v2-auth-dialog v2-alerts-dialog" data-alerts-dialog>
      <h2 class="v2-dialog-title">New yoinks</h2>
      <p class="v2-alerts-sub">From explorers you follow</p>
      <div class="v2-alerts-list" data-alerts-list></div>
      <div class="v2-alerts-actions">
        <button type="button" class="v2-btn-dialog-close v2-alerts-clear" data-alerts-clear>Clear all</button>
        <button type="button" class="v2-btn-dialog-close" data-alerts-close>Close</button>
      </div>
    </dialog>`;
  const btn = el.querySelector('[data-alerts-open]');
  const countEl = el.querySelector('.v2-alerts-count');
  const dialog = el.querySelector('[data-alerts-dialog]');
  const listEl = el.querySelector('[data-alerts-list]');

  function update(state) {
    const count = state.user ? (state.unreadFollows || []).length : 0;
    if (!count) {
      btn.hidden = true;
      return;
    }
    const display = count > UNREAD_CAP ? `${UNREAD_CAP}+` : String(count);
    countEl.textContent = display;
    btn.setAttribute(
      'aria-label',
      `${display} new yoink${count === 1 ? '' : 's'} from explorers you follow`
    );
    btn.hidden = false;
  }

  function onClick(event) {
    if (event.target.closest('[data-alerts-open]')) {
      // Just looking marks nothing — the badge holds its count until rows
      // are opened one by one or "Clear all" resets it
      listEl.innerHTML = (store.get().unreadFollows || []).map(rowHtml).join('');
      if (typeof dialog.showModal === 'function') dialog.showModal();
      else dialog.setAttribute('open', '');
      return;
    }
    if (event.target.closest('[data-alerts-clear]')) {
      markSeen(store);
      dialog.close();
      return;
    }
    if (event.target.closest('[data-alerts-close]')) {
      dialog.close();
      return;
    }
    const row = event.target.closest('[data-alert-yoink]');
    if (row) {
      markSeenOne(store, row.dataset.alertYoink);
      dialog.close();
      router.navigate('yoink', { id: row.dataset.alertYoink });
    }
  }

  el.addEventListener('click', onClick);
  const unsubscribe = store.subscribe(update);
  update(store.get());

  return () => {
    el.removeEventListener('click', onClick);
    unsubscribe();
  };
}
